import React, { useEffect, useState, useRef } from "react";
import { onAuthStateChanged } from "firebase/auth";
import { auth } from "../../config/firebase";
import { useAuth } from "../../hooks/useAuth";
import { ToastContainer } from "react-toastify";
import { useDispatch } from "react-redux";
import { siteConfigService } from "../../services/builder-services/builder.service";
import { applyAdminTheme } from "../../helpers/applyBuilderTheme";
import { setBuilderConfig } from "../../store/slices/builder.slice";
import { appSettingsActions } from "../../store/slices/settings-slices";

const AppInitializer = ({ children }) => {
  const { initializeAuth, getMe, initializationStatus } = useAuth();
  const dispatch = useDispatch();
  const [isFirebaseChecked, setIsFirebaseChecked] = useState(false);
  const [settingsLoaded, setSettingsLoaded] = useState(false);
  const hasInitialized = useRef(false);
  const isValidating = useRef(false);

  useEffect(() => {
    siteConfigService.getAdminConfig().then((adminConfig) => {
      if (!adminConfig) return;
      if (adminConfig?.colors) applyAdminTheme(adminConfig.colors);
      dispatch(setBuilderConfig({ adminConfig }));
    }).catch(() => {});
  }, []);

  useEffect(() => {
    // Prevent multiple initializations
    if (hasInitialized.current) {
      return;
    }
    hasInitialized.current = true;

    // Initialize from localStorage first
    initializeAuth();

    // Listen to Firebase auth state (handles persistence automatically)
    const unsubscribe = onAuthStateChanged(auth, async (firebaseUser) => {
      console.log(
        "Firebase auth state changed:",
        firebaseUser ? "User found" : "No user"
      );

      // Prevent concurrent validation calls
      if (isValidating.current) {
        console.log("Validation already in progress, skipping...");
        return;
      }

      if (firebaseUser) {
        // Firebase has a user (either from login or from persistence after reload)
        isValidating.current = true;
        try {
          // Ensure token is fresh before validating with backend
          await firebaseUser.getIdToken(true);
          console.log(
            "Firebase user authenticated, validating with backend..."
          );

          // Validate with backend and get user data
          await getMe().unwrap();
          console.log("User validated with backend successfully");
          await dispatch(appSettingsActions.getAppSettings());
          setSettingsLoaded(true);
        } catch (error) {
          console.error("Backend validation failed:", error);
          localStorage.removeItem("isAuthenticated");
          localStorage.removeItem("phoneNumber");
          setSettingsLoaded(true); // unblock spinner so login redirect can happen
        } finally {
          isValidating.current = false;
        }
      } else {
        // No Firebase user - clear everything
        console.log("No Firebase user, clearing auth state");
        localStorage.removeItem("isAuthenticated");
        localStorage.removeItem("phoneNumber");
        setSettingsLoaded(true); // no settings needed before redirecting to login
      }

      // Mark Firebase check as complete
      setIsFirebaseChecked(true);
    });

    // Cleanup
    return () => unsubscribe();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // Empty array - only run once on mount

  // Show loading screen while Firebase checks auth state or while backend validates
  if (!isFirebaseChecked || initializationStatus === "loading" || !settingsLoaded) {
    return (
      <div
        style={{
          display: "flex",
          flexDirection: "column",
          justifyContent: "center",
          alignItems: "center",
          height: "100vh",
          backgroundColor: "#f5f5f5",
          fontFamily: "Arial, sans-serif",
        }}
      >
        <div
          style={{
            width: "40px",
            height: "40px",
            border: "4px solid #e0e0e0",
            borderTop: "4px solid #007bff",
            borderRadius: "50%",
            animation: "spin 1s linear infinite",
            marginBottom: "20px",
          }}
        ></div>
        <p
          style={{
            fontSize: "16px",
            color: "#666",
            margin: 0,
          }}
        >
          Initializing application...
        </p>
        <style>
          {`
                        @keyframes spin {
                            0% { transform: rotate(0deg); }
                            100% { transform: rotate(360deg); }
                        }
                    `}
        </style>
      </div>
    );
  }

  return (
    <>
      {children}
      <ToastContainer position="top-center" />
    </>
  );
};

export { AppInitializer };
